import React, { useState, useRef, useEffect } from 'react';
import { Info, X, ChevronDown, Search, Menu, Trash2 } from 'lucide-react';
import { useHistory, useLocation, useParams } from "react-router-dom";
import Tooltip from '../addentities/Tooltip.js';
import { createPortal } from 'react-dom';
import TestSyncModal from '../../components/TestSyncModal.js';
import CharacterLimitedTextarea from '../../components/CharacterLimitedTextarea';
import StockSearchDropdown from '../../components/StockSearchDropdown.js';
import './style.css';
import apiHelper from '../../apiHelper.js';
import AlertNotification from '../../components/AlertNotification.js';
import UnsavedChangesPrompt from '../../components/UnsavedChangesPrompt.js';
import DiscardConfirmationModal from '../../components/DiscardConfirmationModal.js';
import { ReactSortable } from 'react-sortablejs';

const portalRoot = typeof document !== 'undefined' ? document.getElementById('portal-root') : null;

const OptionPortal = ({ children, style }) => {
    if (portalRoot) {
        return createPortal(
            <div className="options-container w-full bg-white border rounded shadow-lg" style={{ ...style, overflow: 'auto', zIndex: 9999 }}>
                {children}
            </div>,
            portalRoot
        );
    }
    return (
        <div className="options-container w-full bg-white border rounded shadow-lg" style={{ ...style, overflow: 'auto', zIndex: 9999 }}>
            {children}
        </div>
    );
};

const SuccessIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ marginRight: '4px', flexShrink: 0 }}>
        <path d="M7.99967 1.3335C4.32367 1.3335 1.33301 4.32416 1.33301 8.00016C1.33301 11.6762 4.32367 14.6668 7.99967 14.6668C11.6757 14.6668 14.6663 11.6762 14.6663 8.00016C14.6663 4.32416 11.6757 1.3335 7.99967 1.3335ZM7.11631 10.4929C6.86809 10.7411 6.46571 10.7413 6.21722 10.4933L4.66331 8.94278C4.40281 8.68284 4.40228 8.26096 4.66213 8.00037C4.92215 7.73962 5.34435 7.73914 5.60496 7.99929L6.66567 9.05816L9.72367 6.00016C9.98398 5.73985 10.406 5.73985 10.6663 6.00016C10.9267 6.26047 10.9267 6.68252 10.6663 6.94283L7.11631 10.4929Z" fill="#146C43" />
    </svg>
);

const FailedIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ marginRight: '4px', flexShrink: 0 }}>
        <path d="M7.96834 1.3335C4.30967 1.3335 1.33301 4.32416 1.33301 8.00016C1.33301 11.6762 4.32367 14.6668 7.99967 14.6668C11.6757 14.6668 14.6663 11.6762 14.6663 8.00016C14.6663 4.32416 11.6617 1.3335 7.96834 1.3335ZM8.66634 10.6668C8.66634 11.035 8.36786 11.3335 7.99967 11.3335C7.63148 11.3335 7.33301 11.035 7.33301 10.6668C7.33301 10.2986 7.63148 10.0002 7.99967 10.0002C8.36786 10.0002 8.66634 10.2986 8.66634 10.6668ZM8.66634 8.00016C8.66634 8.36835 8.36786 8.66683 7.99967 8.66683C7.63148 8.66683 7.33301 8.36835 7.33301 8.00016V5.3335C7.33301 4.96531 7.63148 4.66683 7.99967 4.66683C8.36786 4.66683 8.66634 4.96531 8.66634 5.3335V8.00016Z" fill="#DC3545" />
    </svg>
);

export default function EditListing() {
    const history = useHistory();
    const location = useLocation();
    const { id } = useParams();
    const listing = location.state?.listing;

    // Add debug logging
    console.log('EditListing initialized with:', { id, listingFromState: listing });

    // Track the last modified row ID
    const [lastModifiedRowId, setLastModifiedRowId] = useState(null);

    // Discard confirmation state
    const [discardModalOpen, setDiscardModalOpen] = useState(false);
    const [isDiscarding, setIsDiscarding] = useState(false);
    const [nextLocation, setNextLocation] = useState(null);
    const [showEmptyFieldErrors, setShowEmptyFieldErrors] = useState(false);
    
    const [platformOptions, setPlatformOptions] = useState([]);
    const [idFieldError, setIdFieldError] = useState("");
    const [listings, setListings] = useState([]);

    const breadcrumbs = ["Listing", "Platform Listing", "Edit Listing"];

    const [rows, set_rows] = useState([]);
    const [enableTestSync, setEnableTestSync] = useState(false);
    const [showTestSyncModal, setShowTestSyncModal] = useState(false);
    const [selectStockError, setSelectStockError] = useState("");
    const [alert, setAlert] = useState(null);
    const [showItemAlert, setShowItemAlert] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [duplicateErrors, setDuplicateErrors] = useState({ general: {}, fields: {} });
    const [saveWithErrorsModal, setSaveWithErrorsModal] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [listingId, setListingId] = useState(null);
    const [nextId, setNextId] = useState(1);
    
    // Track which rows have been tested via Test Sync
    const [testedRows, setTestedRows] = useState(new Set());

    // Ref to track if save was attempted with empty rows
    const saveAttemptedWithEmptyRows = useRef(false);

    // Tracks the ID of the original first row so its delete button stays disabled regardless of reorder
    const firstRowId = useRef(null);

    // Test Sync button is enabled only when all rows are complete and have no duplicate errors
    useEffect(() => {
        const hasRows = rows.length > 0;
        const hasNoBlockingErrors = !rows.some(r => {
            const hasEmptyFieldError = !r.platform || !r.product_id || !r.sku_id ||
                r.platform.trim() === '' ||
                r.product_id.trim() === '' ||
                r.sku_id.trim() === '';
            return hasEmptyFieldError || duplicateErrors.general?.[r.id];
        });
        setEnableTestSync(hasRows && hasNoBlockingErrors);
    }, [rows, duplicateErrors]);

    // Initialize nextId based on existing rows
    useEffect(() => {
        if (rows.length > 0) {
            const maxId = Math.max(...rows.map(r => typeof r.id === 'number' ? r.id : 0));
            setNextId(maxId + 1);
        }
    }, [rows]);

    // Trigger validation when save is attempted with empty rows
    useEffect(() => {
        if (saveAttemptedWithEmptyRows.current) {
            set_rows(prev => [...prev]);
            saveAttemptedWithEmptyRows.current = false;
        }
    }, [rows]);

    // SIMPLE add_row function
    const add_row = () => {
        const newId = nextId;
        setNextId(prev => prev + 1);
        
        const newRow = { 
            id: newId, 
            platform: '', 
            product_id: '', 
            sku_id: '', 
            platform_open: false,
            verified: false,
            hasError: false,
            loading: false,
            product_name: '',
            sku_name: '',
            product_price: '-',
            prioduct_stock: '0'
        };
        
        set_rows(prev => [...prev, newRow]);
        setLastModifiedRowId(newId);
        setIsDirty(true);
    };
    
    // Reusable duplicate validation — runs against a given rows array
    const validateDuplicates = (rowsToCheck) => {
        const generalErrors = {};
        const fieldErrors = {};
        for (let i = 0; i < rowsToCheck.length; i++) {
            const row1 = rowsToCheck[i];
            if (!row1.platform) continue;
            for (let j = i + 1; j < rowsToCheck.length; j++) {
                const row2 = rowsToCheck[j];
                if (!row2.platform) continue;
                if (row1.platform === row2.platform) {
                    const productIdMatch = row1.product_id && row2.product_id && row1.product_id === row2.product_id;
                    const skuIdMatch = row1.sku_id && row2.sku_id && row1.sku_id === row2.sku_id;
                    if (productIdMatch || skuIdMatch) {
                        generalErrors[row1.id] = "Duplicate ID found";
                        generalErrors[row2.id] = "Duplicate ID found";
                        if (!fieldErrors[row1.id]) fieldErrors[row1.id] = { productId: false, skuId: false };
                        if (!fieldErrors[row2.id]) fieldErrors[row2.id] = { productId: false, skuId: false };
                        if (productIdMatch) { fieldErrors[row1.id].productId = true; fieldErrors[row2.id].productId = true; }
                        if (skuIdMatch) { fieldErrors[row1.id].skuId = true; fieldErrors[row2.id].skuId = true; }
                    }
                }
            }
        }
        return { general: generalErrors, fields: fieldErrors };
    };

    // Delete function with API call for existing mappings
    const delete_row = async (id, mapping_id, is_unsaved, platform_test_result_id) => {
        console.log('Deleting row:', { id, mapping_id, is_unsaved, platform_test_result_id });
        
        try {
            if (mapping_id && listingId) {
                const response = await apiHelper(
                    `sales/listings/${listingId}/mappings/${mapping_id}/`,
                    "DELETE"
                );
                
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.error || "Failed to delete mapping");
                }
                
                console.log("Mapping deleted successfully from DB");
            }
            
            if (is_unsaved && platform_test_result_id) {
                const response = await apiHelper(
                    `sales/test-results/${platform_test_result_id}/`,
                    "DELETE"
                );
                
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.error || "Failed to delete test result");
                }
                
                console.log("Unsaved test result deleted successfully from DB");
            }
        } catch (error) {
            console.error("Error deleting from database:", error);
            setAlert({
                type: "error",
                action: "delete",
                recordName: "Platform Mapping",
                message: error.message || "Failed to delete mapping from database"
            });
            setShowItemAlert(true);
            return;
        }
        
        // Remove from UI state and re-validate duplicates on the remaining rows
        set_rows(prev => {
            const newRows = prev.filter(r => r.id !== id);
            console.log('Rows after deletion:', newRows);
            setDuplicateErrors(validateDuplicates(newRows));
            return newRows;
        });
        
        // Remove from tested rows set
        setTestedRows(prev => {
            const newSet = new Set(prev);
            newSet.delete(id);
            return newSet;
        });
        
        setIsDirty(true);
    };
    
    // UPDATE row function
    const update_row = (id, changes) => {
        setLastModifiedRowId(id);
        console.log('Updating row:', id, changes);
        
        set_rows(prev => {
            const updated = prev.map(r => {
                if (r.id === id) {
                    return { ...r, ...changes };
                }
                return r;
            });
            
            // Validate duplicates
            const generalErrors = {};
            const fieldErrors = {};
            
            for (let i = 0; i < updated.length; i++) {
                const row1 = updated[i];
                if (!row1.platform) continue;
                
                for (let j = i + 1; j < updated.length; j++) {
                    const row2 = updated[j];
                    if (!row2.platform) continue;
                    
                    if (row1.platform === row2.platform) {
                        const productIdMatch = row1.product_id && row2.product_id && row1.product_id === row2.product_id;
                        const skuIdMatch = row1.sku_id && row2.sku_id && row1.sku_id === row2.sku_id;
                        
                        if (productIdMatch || skuIdMatch) {
                            generalErrors[row1.id] = "Duplicate ID found";
                            generalErrors[row2.id] = "Duplicate ID found";
                            
                            if (!fieldErrors[row1.id]) fieldErrors[row1.id] = { productId: false, skuId: false };
                            if (!fieldErrors[row2.id]) fieldErrors[row2.id] = { productId: false, skuId: false };
                            
                            if (productIdMatch) {
                                fieldErrors[row1.id].productId = true;
                                fieldErrors[row2.id].productId = true;
                            }
                            
                            if (skuIdMatch) {
                                fieldErrors[row1.id].skuId = true;
                                fieldErrors[row2.id].skuId = true;
                            }
                        }
                    }
                }
            }
            
            setDuplicateErrors({ general: generalErrors, fields: fieldErrors });
            return updated;
        });
        
        if (changes.platform !== undefined || changes.product_id !== undefined || changes.sku_id !== undefined) {
            setTestedRows(prev => {
                const newSet = new Set(prev);
                newSet.delete(id);
                return newSet;
            });
        }
        
        setIsDirty(true);
    };
    
    // Function to check for empty rows
    const hasEmptyRows = () => {
        return rows.some(row => {
            return !row.platform || !row.product_id || !row.sku_id || 
                   row.platform.trim() === '' || 
                   row.product_id.trim() === '' || 
                   row.sku_id.trim() === '';
        });
    };

    const hasTestSyncErrors = () => {
        return rows.some(row => row.hasError === true);
    };

    const hasRowErrors = () => {
        return rows.some(row => {
            const hasEmptyFieldError = !row.platform || !row.product_id || !row.sku_id || 
                                      row.platform.trim() === '' || 
                                      row.product_id.trim() === '' || 
                                      row.sku_id.trim() === '';
            const hasDuplicateError = duplicateErrors.general?.[row.id];
            const hasTestSyncError = row.hasError;
            
            return hasEmptyFieldError || hasDuplicateError || hasTestSyncError;
        });
    };
    
    const hasStockInfoErrors = () => {
        return selectStockError !== "" || idFieldError !== "";
    };

    // Close platform dropdown when clicking outside
    useEffect(() => {
        const handler = (e) => {
            if (e.target.closest('.new-form-select-cls')) {
                return;
            }
            if (e.target.closest('.options-container') || (portalRoot && portalRoot.contains(e.target))) {
                return;
            }
            
            set_rows(prev => prev.map(row => {
                if (row.platform_open) {
                    return { ...row, platform_open: false };
                }
                return row;
            }));
        };
        
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const save_ref2 = useRef(null);
    const [error, setError] = useState(null);

    const [save_modal_open_2, set_save_modal_open_2] = useState(false);
    const handle_save_confirm_2 = () => set_save_modal_open_2(false);

    useEffect(() => {
        function outside(e) {
            if (save_ref2.current && !save_ref2.current.contains(e.target))
                set_save_modal_open_2(false);
        }
        if (save_modal_open_2) document.addEventListener('mousedown', outside);
        return () => document.removeEventListener('mousedown', outside);
    }, [save_modal_open_2]);

    const [formData, setFormData] = useState({
        select_stock: null,
        id: "",
        status: "1",
        notes: "",
        stock_id: ""
    });

    // Navigation blocking
    useEffect(() => {
        if (!isDirty || isDiscarding) return;

        const unblock = history.block((tx) => {
            setNextLocation(tx);
            setDiscardModalOpen(true);
            return false;
        });

        return () => unblock();
    }, [isDirty, isDiscarding, history]);

    // Browser/tab close warning
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (isDirty && !isDiscarding) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [isDirty, isDiscarding]);

    // Fetch platforms
    useEffect(() => {
        async function fetchPlatforms() {
            try {
                const response = await apiHelper("sales/platforms/", "GET");
                const apiData = await response.json();
                if (!Array.isArray(apiData)) {
                    setPlatformOptions([]);
                    return;
                }
                const mapped = apiData.map(item => item.platform_name);
                setPlatformOptions(mapped);
            } catch (err) {
                console.error(err);
                setError("Failed to load platforms");
            }
        }
        
        const fetchListings = async () => {
            try {
                const url = "sales/listings/";
                const response = await apiHelper(url, "GET");
                if (!response.ok) {
                    throw new Error("Failed to fetch listings");
                }
                const data = await response.json();
                setListings(data);
            } catch (error) {
                console.error(error);
                alert("Unable to load listings");
            }
        };
        fetchListings();
        fetchPlatforms();
    }, []);

    // Fetch listing data - CRITICAL: Always fetch fresh data
    useEffect(() => {
        async function fetchListingData() {
            console.log('Fetching listing data with:', { id, hasListingState: !!listing });
            
            if (id) {
                // Always fetch fresh data from API
                console.log("Fetching fresh listing data for ID:", id);
                await fetchListingById(id);
            } else if (listing) {
                console.log("Using listing from props:", listing);
                setListingData(listing);
            } else if (location.state?.listing) {
                console.log("Using listing from location.state:", location.state.listing);
                setListingData(location.state.listing);
            } else {
                console.log("No listing data available");
                setAlert({
                    type: "error",
                    action: "fetch",
                    recordName: "Listing",
                    message: "Listing data not available"
                });
                setShowItemAlert(true);
                setIsLoading(false);
            }
        }
        fetchListingData();
    }, [id, listing, location.state]);

    const fetchListingById = async (listingId) => {
        try {
            console.log("Fetching listing by ID:", listingId);
            
            // Try multiple endpoints to ensure we get data
            let listingData = null;
            
            // First try: Get all listings and filter
            try {
                const response = await apiHelper("sales/listings/", "GET");
                if (response.ok) {
                    const data = await response.json();
                    console.log("All listings response:", data);
                    
                    if (Array.isArray(data)) {
                        listingData = data.find(l => 
                            l.sales_listing_id == listingId || 
                            l.id == listingId ||
                            l.listing_id == listingId
                        );
                        console.log("Found listing in all listings:", listingData);
                    }
                }
            } catch (error) {
                console.error("Error fetching all listings:", error);
            }
            
            // If not found, try the detail endpoint
            if (!listingData) {
                try {
                    const response = await apiHelper(`sales/listings/${listingId}/`, "GET");
                    if (response.ok) {
                        listingData = await response.json();
                        console.log("Detail endpoint response:", listingData);
                    }
                } catch (error) {
                    console.error("Error fetching listing detail:", error);
                }
            }
            
            if (!listingData) {
                throw new Error("Listing not found");
            }
            
            console.log("Final listing data:", listingData);
            
            if (listingData && listingData.stock) {
                console.log("Stock data:", listingData.stock);
            }
            
            setListingData(listingData);
        } catch (error) {
            console.error("Error fetching listing:", error);
            setAlert({
                type: "error",
                action: "fetch",
                recordName: "Listing",
                message: "Failed to load listing data"
            });
            setShowItemAlert(true);
        }
    };

    useEffect(() => {
        console.log("Current formData:", formData);
    }, [formData]);

    const setListingData = async (listingData) => {
        if (!listingData) return;
        
        console.log("=== SETTING LISTING DATA ===");
        console.log("Full listingData:", listingData);
        
        const stockId = listingData.stockId || listingData.stock_id;
        
        const selectedStock = {
            id: stockId,
            title: listingData.name,
            sku: listingData.variant,
            sku_code: listingData.sku_code,
            openId: listingData.openId,
            condition: listingData.condition || "NA",
            condition_type: listingData.condition_type || "NA",
            status: listingData.stockStatus || (listingData.status === "Active" ? "Active" : "Inactive"),
            is_archived: listingData.is_archived || false,
        };
        
        console.log("Created selectedStock:", selectedStock);
        
        setFormData((prev) => {
            const newFormData = {
                ...prev,
                select_stock: selectedStock,
                status: listingData.status === "Active" ? "1" : "0",
                id: listingData.iD ?? listingData.listing_code ?? "",
                notes: listingData.notes ?? "",
                stock_id: stockId,
                listing_id: listingData.id ?? listingData.sales_listing_id,
            };
            console.log("New formData:", newFormData);
            return newFormData;
        });
        
        const newListingId = listingData.sales_listing_id || listingData.id;
        console.log("Setting listingId:", newListingId);
        setListingId(newListingId);
        
        // Fetch platform mappings
        await fetchPlatformMappings(newListingId);
    };

    // COMPLETELY REWRITTEN fetchPlatformMappings with better debugging
    const fetchPlatformMappings = async (listingId) => {
        console.log("=== FETCHING PLATFORM MAPPINGS ===");
        console.log("Listing ID:", listingId);
        
        try {
            setIsLoading(true);
            
            // Clear existing rows first
            set_rows([]);
            
            // Try multiple endpoints to get all mapping data
            let allMappings = [];
            let testResults = [];
            let unsavedResults = [];
            
            // Endpoint 1: Get platform mappings
            try {
                console.log("Fetching from platform-mappings endpoint...");
                const response = await apiHelper(`sales/listings/${listingId}/platform-mappings/`, "GET");
                if (response.ok) {
                    const data = await response.json();
                    console.log("Platform mappings response:", data);
                    
                    if (data.success && data.mappings) {
                        allMappings = data.mappings;
                        console.log("Mappings from API:", allMappings);
                    }
                } else {
                    console.error("Platform mappings endpoint failed:", response.status);
                }
            } catch (error) {
                console.error("Error fetching platform mappings:", error);
            }
            
            // Endpoint 2: Get test results
            try {
                console.log("Fetching from test-results endpoint...");
                const response = await apiHelper(`sales/listings/${listingId}/test-results/`, "GET");
                if (response.ok) {
                    const data = await response.json();
                    console.log("Test results response:", data);
                    
                    if (data.success) {
                        if (data.test_results) {
                            testResults = data.test_results;
                        } else if (data.grouped_results) {
                            // API returns grouped_results format — flatten latest_test into each entry
                            testResults = data.grouped_results.map(r => ({
                                platform: r.platform,
                                product_id: r.product_id,
                                sku_id: r.sku_id,
                                ...(r.latest_test || {})
                            }));
                        }
                        if (data.unsaved_test_results) {
                            unsavedResults = data.unsaved_test_results;
                        }
                    }
                }
            } catch (error) {
                console.error("Error fetching test results:", error);
            }
            
            // Build rows from mappings first
            const newRows = [];
            const processedKeys = new Set();
            let maxId = 0;
            const testedIds = new Set();
            
            console.log("Building rows from mappings:", allMappings.length);
            
            // Process each mapping
            allMappings.forEach((mapping, index) => {
                const id = mapping.id || mapping.mapping_id || (index + 1);
                if (id > maxId) maxId = id;
                
                const key = `${mapping.platform}_${mapping.product_id}_${mapping.sku_id}`;
                processedKeys.add(key);
                
                // Find matching test result
                const matchingTest = testResults.find(t => 
                    t.platform === mapping.platform && 
                    t.product_id === mapping.product_id && 
                    t.sku_id === mapping.sku_id
                );
                
                const test = matchingTest?.latest_test || matchingTest;
                
                // Derive verified/hasError/test_status from test result if available,
                // otherwise fall back to the values already stored on the mapping itself.
                const verified = test?.test_status === 'success' || mapping.verified || false;
                const hasError = test?.test_status === 'failed' || mapping.hasError || false;
                const test_status = test?.test_status || (verified ? 'success' : hasError ? 'failed' : 'pending');

                if (test_status !== 'pending') {
                    testedIds.add(id);
                }

                const newRow = {
                    id: id,
                    platform: mapping.platform,
                    product_id: mapping.product_id,
                    sku_id: mapping.sku_id,
                    platform_open: false,
                    verified,
                    hasError,
                    product_name: test?.platform_product_name || mapping.product_name || '',
                    sku_name: test?.platform_sku_name || mapping.sku_name || '',
                    product_price: test?.platform_price ? `SGD ${test.platform_price}` : (mapping.product_price || '-'),
                    prioduct_stock: test?.platform_stock_quantity?.toString() || mapping.prioduct_stock || '0',
                    errorMessage: test?.error_message || mapping.errorMessage,
                    mapping_id: mapping.id,
                    sort_order: mapping.sort_order !== undefined ? mapping.sort_order : index,
                    test_status
                };
                
                console.log(`Created row from mapping:`, newRow);
                newRows.push(newRow);
            });
            
            // Add unsaved test results that aren't in mappings
            console.log("Processing unsaved results:", unsavedResults.length);
            unsavedResults.forEach((result, index) => {
                const key = `${result.platform}_${result.product_id}_${result.sku_id}`;
                
                if (!processedKeys.has(key)) {
                    const id = maxId + index + 1;
                    
                    const newRow = {
                        id: id,
                        platform: result.platform,
                        product_id: result.product_id,
                        sku_id: result.sku_id,
                        platform_open: false,
                        verified: result.verified || false,
                        hasError: result.hasError || false,
                        product_name: result.product_name || '',
                        sku_name: result.sku_name || '',
                        product_price: result.product_price || '-',
                        prioduct_stock: result.prioduct_stock || '0',
                        errorMessage: result.errorMessage,
                        is_unsaved: true,
                        test_result_id: result.test_result_id,
                        platform_test_result_id: result.platform_test_result_id,
                        test_status: result.test_status || 'pending',
                        sort_order: newRows.length // Add at the end
                    };
                    
                    console.log(`Created row from unsaved result:`, newRow);
                    newRows.push(newRow);
                    processedKeys.add(key);
                    
                    if (result.verified || result.hasError) {
                        testedIds.add(id);
                    }
                }
            });
            
            // Sort by sort_order
            newRows.sort((a, b) => {
                if (a.sort_order !== undefined && b.sort_order !== undefined) {
                    return a.sort_order - b.sort_order;
                }
                return 0;
            });
            
            console.log("Final rows to set:", newRows);
            console.log("Tested IDs:", Array.from(testedIds));
            
            set_rows(newRows);
            // Lock in the first row's ID so its delete button stays disabled even after reorder
            if (newRows.length > 0 && firstRowId.current === null) {
                firstRowId.current = newRows[0].id;
            }
            setNextId(maxId + newRows.length + 1);
            setTestedRows(testedIds);
            
            if (newRows.length === 0) {
                console.log("No rows found, adding default empty row?");
                // Optionally add an empty row if no data
                // add_row();
            }
            
        } catch (error) {
            console.error("Error in fetchPlatformMappings:", error);
            setAlert({
                type: "error",
                action: "fetch",
                recordName: "Platform Mappings",
                message: "Failed to load platform mappings"
            });
            setShowItemAlert(true);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchTestResults = async (listingId, currentRows) => {
        try {
            const response = await apiHelper(`sales/listings/${listingId}/test-results/`, "GET");
            if (!response.ok) return;
            const data = await response.json();
            
            if (data.success && data.grouped_results) {
                const currentRowsMap = new Map();
                (currentRows || rows).forEach(row => {
                    if (row.platform && row.product_id && row.sku_id) {
                        const key = `${row.platform}_${row.product_id}_${row.sku_id}`;
                        currentRowsMap.set(key, row.id);
                    }
                });
                
                set_rows(prev => {
                    const existingRowsMap = new Map();
                    prev.forEach(row => {
                        const key = `${row.platform}_${row.product_id}_${row.sku_id}`;
                        existingRowsMap.set(key, row);
                    });
                    
                    const updated = prev.map(row => {
                        const key = `${row.platform}_${row.product_id}_${row.sku_id}`;
                        const matchingResult = data.grouped_results.find(
                            r => r.platform === row.platform && 
                                 r.product_id === row.product_id && 
                                 r.sku_id === row.sku_id
                        );
                        
                        if (matchingResult && matchingResult.latest_test) {
                            const test = matchingResult.latest_test;
                            return {
                                ...row,
                                verified: test.test_status === 'success',
                                hasError: test.test_status === 'failed',
                                product_name: test.platform_product_name || row.product_name,
                                sku_name: test.platform_sku_name || row.sku_name,
                                product_price: test.platform_price ? `SGD ${test.platform_price}` : row.product_price,
                                prioduct_stock: test.platform_stock_quantity?.toString() || row.prioduct_stock,
                                errorMessage: test.error_message,
                                test_status: test.test_status,
                                mapping_id: test.mapping_id || row.mapping_id
                            };
                        }
                        return row;
                    });
                    
                    return updated;
                });
                
                setTestedRows(prev => {
                    const newSet = new Set(prev);
                    data.grouped_results.forEach(result => {
                        if (result.latest_test && result.latest_test.test_status !== 'pending') {
                            const key = `${result.platform}_${result.product_id}_${result.sku_id}`;
                            const matchingRowId = currentRowsMap.get(key);
                            if (matchingRowId) {
                                newSet.add(matchingRowId);
                            }
                        }
                    });
                    return newSet;
                });
            }
        } catch (error) {
            console.error("Error fetching test results:", error);
        }
    };

    // Check if selected stock is already used
    const checkStockAlreadyUsed = (selectedStock) => {
        if (!selectedStock) return false;
        
        const existingListing = listings.find(listing => 
            listing.stock_id === selectedStock.id && 
            listing.sales_listing_id != formData.listing_id
        );
        
        return !!existingListing;
    };

    const handleSaveExit = async () => {
        setIsSaving(true);
        
        console.log("Save check - selected stock:", formData.select_stock);
        
        setShowEmptyFieldErrors(true);
        
        if (duplicateErrors.general && Object.keys(duplicateErrors.general).length > 0) {
            console.log('Blocking save due to duplicate errors');
            setAlert({
                type: "error",
                action: "update",
                recordName: "Listing",
                message: "Error Saving"
            });
            setShowItemAlert(true);
            setIsSaving(false);
            return;
        }
        
        const emptyRowsExist = hasEmptyRows();
        if (emptyRowsExist) {
            console.log("Empty rows detected");
            setAlert({
                type: "error",
                action: "update",
                recordName: "Listing",
                message: "Please fill in all required fields in Platform Mapping"
            });
            setShowItemAlert(true);
            setIsSaving(false);
            return;
        }
        
        if (formData.select_stock && 
            formData.select_stock.listed && 
            formData.select_stock.id !== formData.stock_id) {
            console.log("Save blocked - different listed stock");
            setSelectStockError("This Stock is already listed. Please select another.");
            setAlert({
                type: "error",
                action: "update",
                recordName: "Listing",
                message: "Error Saving"
            });
            setShowItemAlert(true);
            setIsSaving(false);
            return;
        }
        
        if (formData.select_stock && formData.select_stock.status === "Inactive") {
            console.log("Save blocked - inactive stock");
            setSelectStockError("Stock does not exist. Please select another.");
            setAlert({
                type: "error",
                action: "update",
                recordName: "Listing",
                message: "Error Saving"
            });
            setShowItemAlert(true);
            setIsSaving(false);
            return;
        }
        
        if (hasStockInfoErrors()) {
            setAlert({
                type: "error",
                action: "update",
                recordName: "Listing",
                message: "Error Saving - Please fix validation errors"
            });
            setShowItemAlert(true);
            setIsSaving(false);
            return;
        }

        if (!formData.select_stock) {
            setSelectStockError("Missing required information");
            setIsSaving(false);
            return;
        } else {
            setSelectStockError("");
        }

        const hasErrors = hasRowErrors();
        console.log("hasErrors result:", hasErrors);

        if (hasErrors) {
            setSaveWithErrorsModal(true);
            setIsSaving(false);
            return;
        }

        setShowEmptyFieldErrors(false);
        await updateListing(false);
    };

    const updateListing = async (hasErrors = false) => {
        try {
            setShowEmptyFieldErrors(false);
            
            const platformMappings = rows
                .filter(row => row.platform && row.product_id && row.sku_id)
                .map((row, index) => {
                    let platformPrice = null;
                    if (row.product_price && row.product_price !== '-' && row.product_price !== 'Invalid ID') {
                        try {
                            const priceString = row.product_price.replace(/[^\d.-]/g, '');
                            const parsed = parseFloat(priceString);
                            platformPrice = isNaN(parsed) ? null : parsed;
                        } catch (error) {
                            console.error('Error parsing price:', error);
                            platformPrice = null;
                        }
                    }

                    let platformStockQuantity = 0;
                    if (row.prioduct_stock && row.prioduct_stock !== '-' && row.prioduct_stock !== 'Invalid ID') {
                        try {
                            const stockString = row.prioduct_stock.replace(/[^\d]/g, '');
                            const parsed = parseInt(stockString, 10);
                            platformStockQuantity = isNaN(parsed) ? 0 : parsed;
                        } catch (error) {
                            console.error('Error parsing stock:', error);
                            platformStockQuantity = 0;
                        }
                    }

                    return {
                        platform: row.platform,
                        product_id: row.product_id,
                        sku_id: row.sku_id,
                        platform_product_name: row.product_name || '',
                        platform_sku_name: row.sku_name || '',
                        platform_price: platformPrice,
                        platform_stock_quantity: platformStockQuantity,
                        test_status: row.hasError ? 'failed' : (row.verified ? 'success' : 'pending'),
                        mapping_id: row.mapping_id,
                        was_tested: testedRows.has(row.id)
                    };
                });

            const payload = {
                listing_code: formData.id || null,
                status: formData.status,
                notes: formData.notes || '',
                stock_id: formData.select_stock?.id,
                platform_mappings: platformMappings
            };

            console.log("Updating listing with payload:", payload);

            const response = await apiHelper(
                `sales/listings/${listingId}/update-with-mappings/`,
                "PUT",
                payload
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                setAlert({
                    type: "error",
                    action: "update",
                    recordName: "Listing",
                    message: errorData.error || "Failed to update listing"
                });
                setShowItemAlert(true);
                setIsSaving(false);
                return;
            }

            setIsDirty(false);
            
            // Navigate with cache-busting
            setTimeout(() => {
                history.push("/listing-pricing", {
                    success: hasErrors ? "Listing saved with Error" : "Listing Updated Successfully",
                    type: hasErrors ? "warning" : "success",
                    timestamp: Date.now(),
                    refresh: true
                });
            }, 100);

        } catch (error) {
            console.error("Error in updateListing:", error);
            setAlert({
                type: "error",
                action: "update",
                recordName: "Listing",
                message: error.message || "An unexpected error occurred while saving"
            });
            setShowItemAlert(true);
            setIsSaving(false);
        } finally {
            setIsSaving(false);
        }
    };


    const handle_select_stock_select = (opt) => {
        setFormData((prev) => ({ ...prev, select_stock: opt }));
        if (opt.listed && opt.id !== formData.stock_id) {
            setSelectStockError("This Stock is already listed. Please select another.");
        } else if (opt.status === "Inactive") {
            setSelectStockError("Stock does not exist. Please select another.");
        } else {
            setSelectStockError("");
        }
        setIsDirty(true);
    };

    const handleStockBlur = () => {
        if (!formData.select_stock) {
            setSelectStockError("Missing required information");
            return;
        }
        if (formData.select_stock?.listed && formData.select_stock.id !== formData.stock_id) {
            setSelectStockError("This Stock is already listed. Please select another.");
            return;
        }
        if (formData.select_stock?.status === "Inactive") {
            setSelectStockError("Stock does not exist. Please select another.");
            return;
        }
        setSelectStockError("");
    };

    const handleCancel = () => {
        history.push("/listing-pricing");
    };

    const handleDiscardConfirm = (confirm) => {
        if (confirm) {
            console.log("Discard confirmed");
            setIsDiscarding(true);
            setIsDirty(false);
            setDiscardModalOpen(false);

            setTimeout(() => {
                if (nextLocation) {
                    const { pathname, state } = nextLocation.location || nextLocation;
                    const unblock = history.block(() => null);
                    
                    history.push(pathname, { 
                        ...state,
                        section: state?.section || localStorage.getItem('selectedModule') || "Sales",
                        success: "Listing Discarded"
                    });
                    
                    setTimeout(() => unblock(), 0);
                    setNextLocation(null);
                } else {
                    history.push("/listing-pricing", { 
                        success: "Listing Discarded",
                        section: "Sales"
                    });
                }
            }, 100);
        } else {
            setDiscardModalOpen(false);
            setNextLocation(null);
        }
    };

    const validateIdField = () => {
        if (formData.id && formData.id.length > 50) {
            setIdFieldError(" ");
            return false;
        }
        if(listings.find(l => l.listing_code == formData.id && l.sales_listing_id != formData.listing_id)) {
            setIdFieldError("Listing ID already exists");
            return false;
        }
        setIdFieldError("");
        return true;
    };
    
    const handleIdBlur = () => {
        validateIdField();
    };

    // Debug effect to log rows changes
    useEffect(() => {
        console.log("Rows state updated:", rows);
    }, [rows]);

    if (isLoading) {
        return <div className="loading-container">Loading listing data...</div>;
    }

    return (
        <>
            <div className="company-body main-company-body-inner new-look" style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden',
            }}>
                {/* Breadcrumb */}
                <div className="breadcrumb">
                    {breadcrumbs.map((crumb, index) => {
                        const isLast = index === breadcrumbs.length - 1;
                        return (
                            <span
                                key={index}
                                className={`breadcrumb-item ${crumb === "Platform Listing" ? "clickable" : ""}`}
                                onClick={() => { if (crumb === "Platform Listing") history.push('/listing-pricing'); }}
                                style={{ cursor: crumb === "Platform Listing" ? 'pointer' : 'default' }}
                            >
                                {crumb}{!isLast ? '  ' : ''}
                            </span>
                        );
                    })}
                </div>
                
                {showItemAlert && alert && (
                    <AlertNotification
                        type={alert.type}
                        action={alert.action}
                        recordName={alert.recordName}
                        message={alert.message}
                        onClose={() => setShowItemAlert(false)}
                    />
                )}

                <DiscardConfirmationModal
                    isOpen={discardModalOpen}
                    onCancel={() => {
                        setDiscardModalOpen(false);
                        setNextLocation(null);
                    }}
                    onConfirm={handleDiscardConfirm}
                    entityName="Listing"
                />

                <div className='edit-organisation-info'>
                    <div className='edit-organisation-left'>
                        <h1>Edit Listing</h1>
                    </div>
                    <div className='discard-save-organisation-right d-flex align-items-center justify-content-end flex-wrap gap-2'>
                        <button className="dark-button" type='button' onClick={handleCancel}>Cancel</button>
                        
                        <TestSyncModal
                            isOpen={showTestSyncModal}
                            onClose={() => setShowTestSyncModal(false)}
                            onConfirm={(updatedRows) => {
                                if (updatedRows) {
                                    set_rows(updatedRows);
                                }
                                setTestedRows(prev => {
                                    const newSet = new Set(prev);
                                    rows.forEach(row => {
                                        if (row.platform && row.product_id && row.sku_id) {
                                            newSet.add(row.id);
                                        }
                                    });
                                    return newSet;
                                });
                                setShowTestSyncModal(false);
                            }}
                            rows={rows}
                            setRows={set_rows}
                            selectedStock={formData.select_stock}
                            listingId={listingId}
                        />
                        
                        <button 
                            className="dark-border" 
                            type="button" 
                            onClick={handleSaveExit}
                            disabled={isSaving}
                            style={{ opacity: isSaving ? 0.6 : 1, cursor: isSaving ? 'not-allowed' : 'pointer' }}
                        >
                            {isSaving ? 'Saving...' : 'Save & Exit'}
                        </button>
                    </div>
                </div>
                
                <UnsavedChangesPrompt isDirty={isDirty && !isDiscarding} entityName="Listing" />

                {saveWithErrorsModal && (
                    <>
                        <div className="drawer-overlay" />
                        <div className="entity-drawer modal-popup-cls" style={{ 
                            minWidth: "650px", 
                            margin: '0 auto', 
                            background: 'white', 
                            borderRadius: 12, 
                            zIndex: 1050, 
                            padding: 24, 
                            position: 'fixed', 
                            top: '50%', 
                            left: '50%', 
                            transform: 'translate(-50%, -50%)', 
                            boxShadow: '0px 2px 10px rgba(0,0,0,0.2)', 
                            height: 'auto', 
                            right: 'auto', 
                            overflow: 'inherit' 
                        }}>
                            <div className="drawer-header d-flex justify-content-end align-items-center pt-0 pe-0">
                                <button className="close-btn" onClick={() => setSaveWithErrorsModal(false)}>
                                    <X size={20} />
                                </button>
                            </div>
                            <h3 style={{ 
                                textAlign: 'left', 
                                color: '#212529', 
                                fontSize: '20px', 
                                fontWeight: '600', 
                                lineHeight: '24px', 
                                fontFamily: 'Montserrat', 
                                marginBottom: '15px' 
                            }}>
                                Save Listing With Errors?
                            </h3>
                            <p style={{ 
                                color: "#6C757D",
                                fontSize: "14px",
                                fontWeight: 400,
                                lineHeight: "18px", 
                            }}>
                                Listings with invalid Product ID or SKU ID can't be mapped, so actions that require syncing to e-commerce platforms such as pricing updates won't work
                            </p>
                            <div className="drawer-footer" style={{ 
                                paddingTop: '10px', 
                                display: 'flex', 
                                justifyContent: 'flex-end', 
                                gap: '10px' 
                            }}>
                                <button 
                                    className="dark-button" 
                                    onClick={() => setSaveWithErrorsModal(false)}
                                    style={{
                                        display: "flex",
                                        height: "34px",
                                        minWidth: "100px",
                                        padding: "10px",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        gap: "10px",
                                        borderRadius: "8px",
                                        border: "1px solid  var(--navbar-dropdown-active, #212529)",
                                        background: "var(--Background-White, #FFF)",
                                        boxShadow: "0 1px 4px 0 rgba(0, 0, 0, 0.10)",
                                        color: "var(--navbar-dropdown-active, #212529)",
                                        fontFamily: "Montserrat",
                                        fontSize: "12px",
                                        fontWeight: 600,
                                        fontStyle: "normal",
                                        lineHeight: "normal",
                                    }}
                                >
                                    Cancel
                                </button>
                                <button 
                                    className="btn-save" 
                                    onClick={async () => {
                                        setIsSaving(true);
                                        setSaveWithErrorsModal(false);
                                        await updateListing(true);
                                    }}
                                    style={{
                                        display: "flex",
                                        height: "34px",
                                        minWidth: "100px",
                                        padding: "10px",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        gap: "10px",
                                        borderRadius: "8px",
                                        background: "var(--navbar-dropdown-active, #212529)",
                                        boxShadow: "0 1px 4px 0 rgba(0, 0, 0, 0.10)",
                                        color: "var(--Background-White, #FFF)",
                                        fontFamily: "Montserrat",
                                        fontSize: "12px",
                                        fontWeight: 600,
                                        fontStyle: "normal",
                                        lineHeight: "normal",
                                    }}
                                >
                                    Save
                                </button>
                            </div>
                        </div>
                    </>
                )}

                <div style={{
                    flex: 1,
                    overflowY: 'auto',
                    paddingBottom: '24px',
                }}>
                    <div className="employee-tabs-container">
                        {save_modal_open_2 && (
                            <>
                                <div className="drawer-overlay" />
                                <div className="entity-drawer modal-popup-cls" ref={save_ref2} style={{ maxWidth: 650, margin: '0 auto', background: 'white', borderRadius: 12, zIndex: 1050, padding: 24, position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', boxShadow: '0px 2px 10px rgba(0,0,0,0.2)', height: 'auto', right: 'auto', overflow: 'inherit' }}>
                                    <div className="drawer-header d-flex justify-content-end align-items-center pt-0 pe-0">
                                        <button className="close-btn" onClick={() => set_save_modal_open_2(false)}><X size={20} /></button>
                                    </div>
                                    <h3 style={{ textAlign: 'left', color: '#212529', fontSize: '20px', fontWeight: '600', lineHeight: '24px', fontFamily: 'Montserrat', marginBottom: '5px' }}>The edit function implementation</h3>
                                    <div className="drawer-footer" style={{ paddingTop: '10px' }}>
                                        <button className="btn-save" onClick={handle_save_confirm_2}>OK</button>
                                    </div>
                                </div>
                            </>
                        )}

                        <div className="tab-content" id="edit-spu-content">
                            <div className="info-section">
                                {/* Stock Info Card */}
                                <div className="info-card new-info-overflow-auto">
                                    <div className="info-header"><h4 className="info-title">Stock Info</h4></div>
                                    <div className="info-content d-block">
                                        <div className='grid-info-adding'>
                                            <div className='row gappingcls-added'>
                                                <div className="col-lg-6 col-md-6 col-12">
                                                    <div className="info-inner">
                                                        <label htmlFor="select_stock" className="form-label d-flex align-items-center gap-1">
                                                            Select Stock<span style={{ color: '#DC3545' }}>*</span>
                                                            <Tooltip title="Select Stock" body="Select a Stock for Listing and Platform Mapping"><Info size={16} className="info-icon" /></Tooltip>
                                                        </label>

                                                        
                                                        <StockSearchDropdown
                                                            selected={formData.select_stock}
                                                            onSelect={handle_select_stock_select}
                                                            onClear={() => {
                                                                setFormData((prev) => ({ ...prev, select_stock: null }));
                                                                setSelectStockError("");
                                                                setIsDirty(true);
                                                            }}
                                                            onClose={handleStockBlur}
                                                            error={selectStockError}
                                                        />
                                                        {selectStockError && <div className="mt-1" style={{ fontSize: "12px", color: '#DC3545' }}>{selectStockError}</div>}
                                                    </div>
                                                </div>
                                                <div className='col-lg-6 col-md-6 col-12'>
                                                    <div className="info-inner">
                                                        <label htmlFor="status_active" className="form-label">Status <span style={{ color: '#DC3545' }}>*</span></label>
                                                        <div className='flexing-type-radio'>
                                                            <div className="form-check">
                                                                <input className="form-check-input" type="radio" id="status_active" name="status" value="1" 
                                                                    checked={formData.status === '1'} onChange={(e) => { setFormData({ ...formData, status: e.target.value }); setIsDirty(true); }} />
                                                                <label className="form-check-label" htmlFor="status_active" style={{ fontWeight: 'normal', fontSize: '14px' }}>Active</label>
                                                            </div>
                                                            <div className="form-check">
                                                                <input className="form-check-input" type="radio" id="status_inactive" name="status" value="0" 
                                                                    checked={formData.status === '0'} onChange={(e) => { setFormData({ ...formData, status: e.target.value }); setIsDirty(true); }} />
                                                                <label className="form-check-label" htmlFor="status_inactive" style={{ fontWeight: 'normal', fontSize: '14px' }}>Inactive</label>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className='row gappingcls-added'>
                                                <div className='col-lg-6 col-md-6 col-12'>
                                                    <div className="info-inner">
                                                        <label htmlFor="id" className="form-label d-flex align-items-center gap-1">ID</label>
                                                        <style>{`
                                                            .edit-listing-id-field input.form-control {
                                                                border: 1px solid #6C757D !important;
                                                            }
                                                            .edit-listing-id-field.has-error input.form-control,
                                                            .edit-listing-id-field.has-error input.form-control.is-invalid {
                                                                border: 1px solid #DC3545 !important;
                                                            }
                                                            .edit-listing-id-field.has-error .character-counter {
                                                                color: #DC3545 !important;
                                                            }
                                                            .edit-listing-id-field.has-error span {
                                                                color: #DC3545 !important;
                                                            }
                                                        `}</style>
                                                        <div className={`edit-listing-id-field${idFieldError ? ' has-error' : ''}`}>
                                                            <CharacterLimitedTextarea
                                                                id="id" name="id" value={formData.id}
                                                                onBlur={handleIdBlur}
                                                                onChange={(e) => { setFormData({ ...formData, id: e.target.value }); setIsDirty(true); }}
                                                                maxLength={50} counterPosition="end"
                                                            />
                                                        </div>
                                                        {idFieldError && (
                                                            <div
                                                                className="d-block"
                                                                style={{ marginTop: "4px", color: "#DC3545" }}
                                                            >
                                                                {idFieldError}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Platform Mapping Card */}
                                <div className="info-card new-info-overflow-auto main-company-body-inner">
                                    <div className="info-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                        <h4 className="info-title">Platform Mapping</h4>
                                        <button 
                                            className={!enableTestSync ? 'disabled' : ''} 
                                            style={{ 
                                                borderRadius: '8px', background: '#ffffff', padding: '10px', fontSize: '12px', 
                                                border: "1px solid #212529", height: "34px", minWidth: "100px", 
                                                display: "flex", justifyContent: "center", alignItems: "center", gap: '10px', 
                                                fontFamily: "Montserrat", fontSize: '12px', fontWeight: "600", 
                                                cursor: enableTestSync ? 'pointer' : 'not-allowed', opacity: enableTestSync ? 1 : 0.5
                                            }} 
                                            onClick={() => { if (enableTestSync) setShowTestSyncModal(true); }}
                                        >
                                            Test Sync
                                        </button>
                                    </div>
                                    <div className="info-content d-block entities-tab">
                                        <div style={{ padding: '20px', boxShadow: '0px 1px 4px #0000001A', borderRadius: '12px' }}>
                                            <div className="table-wrapper" style={{ position: 'relative', overflowX: 'auto' }}>
                                                <table className="entities-table tab-table w-100">
                                                    <thead height="35px">
                                                        <tr>
                                                            <th />
                                                            <th><span>Platform <span style={{ color: "#DC3545" }}>*</span></span></th>
                                                            <th><span>Product ID <span style={{ color: "#DC3545" }}>*</span></span></th>
                                                            <th><span>SKU ID <span style={{ color: "#DC3545" }}>*</span></span></th>
                                                            <th />
                                                        </tr>
                                                    </thead>
                                                    <ReactSortable 
                                                        list={rows} 
                                                        setList={set_rows} 
                                                        handle=".drag-handle"
                                                        tag="tbody"
                                                        animation={150}
                                                        onEnd={() => setIsDirty(true)}
                                                    >
                                                        {rows.map((r, index) => (
                                                            <PlatformRow 
                                                                key={r.id}
                                                                row_state={r}
                                                                delete_row={delete_row}
                                                                update_row={update_row}
                                                                platform_options={platformOptions}
                                                                SuccessIcon={SuccessIcon}
                                                                FailedIcon={FailedIcon}
                                                                duplicateErrors={duplicateErrors}
                                                                setDuplicateErrors={setDuplicateErrors}
                                                                listingId={listingId}
                                                                isFirstRow={index === 0}
                                                                showEmptyFieldErrors={showEmptyFieldErrors}
                                                            />
                                                        ))}
                                                    </ReactSortable>
                                                </table>
                                            </div>
                                            <div className="edit-organisation-info" style={{ display: 'flex', marginTop: '20px', marginBottom: '0', boxShadow: 'none', padding: '0' }}>
                                                <button onClick={add_row} className="dark-button">Add Mapping</button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Additional Info Card */}
                                <div className="info-card">
                                    <div className="info-header"><h4 className="info-title">Additional Info</h4></div>
                                    <div className="info-content">
                                        <div className="info-inner common-textarea">
                                            <label htmlFor="notes" className="form-label">Note</label>
                                            <textarea className="form-control w-100 common-textarea-all" id="notes" rows="3" name="notes" 
                                                value={formData.notes} onChange={(e) => { setFormData({ ...formData, notes: e.target.value }); setIsDirty(true); }} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

// PlatformRow component - unchanged from your original
function PlatformRow({ 
    row_state: r, 
    delete_row, 
    update_row, 
    platform_options, 
    SuccessIcon, 
    FailedIcon, 
    duplicateErrors,
    setDuplicateErrors,
    listingId,
    isFirstRow,
    showEmptyFieldErrors
}) {
    const platform_button_ref = useRef(null);
    const [portal_style, set_portal_style] = useState({});
    const [hoveredOption, setHoveredOption] = useState(null);
    const [emptyFieldErrors, setEmptyFieldErrors] = useState({
        platform: false,
        product_id: false,
        sku_id: false
    });
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        if (r.platform_open && platform_button_ref.current) {
            const rect = platform_button_ref.current.getBoundingClientRect();
            set_portal_style({
                position: 'absolute',
                top: rect.bottom + window.scrollY + 8,
                left: rect.left + window.scrollX,
                width: Math.max(280, rect.width),
                maxHeight: 270,
                overflowY: 'auto',
                zIndex: 9999,
            });
        }
    }, [r.platform_open]);

    const validateEmptyFields = () => {
        const errors = {
            platform: !r.platform || r.platform.trim() === '',
            product_id: !r.product_id || r.product_id.trim() === '',
            sku_id: !r.sku_id || r.sku_id.trim() === ''
        };
        setEmptyFieldErrors(errors);
        return errors;
    };

    const hasEmptyFieldError = emptyFieldErrors.platform || emptyFieldErrors.product_id || emptyFieldErrors.sku_id;
    const hasBlockingError = duplicateErrors?.general?.[r.id] || hasEmptyFieldError;
    
    const showFailedIcon = r.hasError && !duplicateErrors?.general?.[r.id];
    const showSuccessIcon = r.verified && !r.hasError && !duplicateErrors?.general?.[r.id];

    const getPlatformErrorMessage = () => {
        if (showEmptyFieldErrors && emptyFieldErrors.platform) return "Empty Fields";
        return "";
    };

    const getProductIdErrorMessage = () => {
        if (duplicateErrors?.fields?.[r.id]?.productId) return "ID already used for this platform";
        if (showEmptyFieldErrors && emptyFieldErrors.product_id) return "Empty Fields";
        if (r.hasError && r.product_name === 'Invalid ID') return "Invalid ID";
        return "";
    };

    const getSkuIdErrorMessage = () => {
        if (duplicateErrors?.fields?.[r.id]?.skuId) return "ID already used for this platform";
        if (showEmptyFieldErrors && emptyFieldErrors.sku_id) return "Empty Fields";
        if (r.hasError && (r.sku_name === 'Invalid ID' || r.product_name === 'Invalid ID')) return "Invalid ID";
        return "";
    };

    const handleUpdateRow = (id, changes) => {
        const isFieldChange = changes.platform !== undefined || changes.product_id !== undefined || changes.sku_id !== undefined;

        // Clear verified/hasError only when user edits a field (platform, product_id, sku_id)
        update_row(id, {
            ...changes,
            ...(isFieldChange && { verified: false, hasError: false, errorMessage: null })
        });

        if (isFieldChange) {
            setTimeout(() => {
                const updatedRow = { ...r, ...changes };
                const errors = {
                    platform: !updatedRow.platform || updatedRow.platform.trim() === '',
                    product_id: !updatedRow.product_id || updatedRow.product_id.trim() === '',
                    sku_id: !updatedRow.sku_id || updatedRow.sku_id.trim() === ''
                };
                setEmptyFieldErrors(errors);
            }, 0);
        }
    };

    const handlePlatformClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleUpdateRow(r.id, { platform_open: !r.platform_open });
    };

    const handleDeleteClick = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (isFirstRow) return;
        if (isDeleting) return;
        
        setIsDeleting(true);
        await delete_row(r.id, r.mapping_id, r.is_unsaved, r.platform_test_result_id);
        setIsDeleting(false);
    };

    useEffect(() => {
        if (showEmptyFieldErrors) {
            validateEmptyFields();
        }
    }, [r.platform, r.product_id, r.sku_id, showEmptyFieldErrors]);

    const getPlatformBorderColor = () => {
        if (showEmptyFieldErrors && emptyFieldErrors.platform) return '#DC3545';
        return '#6C757D';
    };

    const getProductIdBorderColor = () => {
        if (duplicateErrors?.fields?.[r.id]?.productId) return '#DC3545';
        if (showEmptyFieldErrors && emptyFieldErrors.product_id) return '#DC3545';
        if (r.hasError && r.product_name === 'Invalid ID') return '#DC3545';
        return '#6C757D';
    };

    const getSkuIdBorderColor = () => {
        if (duplicateErrors?.fields?.[r.id]?.skuId) return '#DC3545';
        if (showEmptyFieldErrors && emptyFieldErrors.sku_id) return '#DC3545';
        if (r.hasError && (r.sku_name === 'Invalid ID' || r.product_name === 'Invalid ID')) return '#DC3545';
        return '#6C757D';
    };

    return (
        <tr>
            <td style={{ width: '60px', verticalAlign: 'top', paddingTop: '12px' }}>
                <button 
                    className="btn p-0" 
                    onClick={handleDeleteClick}
                    type="button"
                    style={{ 
                        boxShadow: 'none',
                        opacity: isFirstRow ? 0.3 : (isDeleting ? 0.5 : 1),
                        cursor: isFirstRow ? 'not-allowed' : (isDeleting ? 'not-allowed' : 'pointer')
                    }}
                    disabled={isFirstRow || isDeleting}
                    title={isFirstRow ? "First row cannot be deleted" : "Delete row"}
                >
                    <Trash2 size={18} />
                </button>
            </td>
            
            <td style={{ position: 'relative', minWidth: '200px', maxWidth: '200px', verticalAlign: 'top', paddingTop: '8px' }}>
                <div>
                    <div 
                        ref={platform_button_ref} 
                        className={`form-control new-form-select-cls ${!r.platform ? 'placeholder' : ''}`}
                        onClick={handlePlatformClick}
                        style={{
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '0.5rem',
                            minHeight: '40px',
                            background: 'none',
                            borderColor: getPlatformBorderColor(),
                        }}
                    >
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginRight: 'auto', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {showSuccessIcon && <SuccessIcon />}
                            {showFailedIcon && <FailedIcon />}
                            <span style={{ color: !r.platform ? '#6C757D' : 'inherit' }}>
                                {r.platform || 'Select platform'}
                            </span>
                        </span>
                        <ChevronDown size={16} style={{ transform: r.platform_open ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s ease', flexShrink: 0 }} />
                    </div>
                    <div style={{ color: '#DC3545', fontSize: '12px', marginTop: '4px', paddingLeft: '4px', minHeight: '18px' }}>
                        {getPlatformErrorMessage()}
                    </div>
                </div>
                {r.platform_open && (
                    <OptionPortal style={portal_style}>
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                            {platform_options.map((opt, i) => (
                                <li
                                    key={i}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleUpdateRow(r.id, { platform: opt, platform_open: false });
                                    }}
                                    onMouseEnter={() => setHoveredOption(i)}
                                    onMouseLeave={() => setHoveredOption(null)}
                                    style={{
                                        padding: '10px',
                                        cursor: 'pointer',
                                        borderRadius: 6,
                                        fontFamily: 'Montserrat',
                                        color: '#212529',
                                        whiteSpace: 'nowrap',
                                        backgroundColor: hoveredOption === i ? '#F8F9FA' : 'transparent',
                                    }}
                                >
                                    {opt}
                                </li>
                            ))}
                        </ul>
                    </OptionPortal>
                )}
            </td>
            
            <td style={{ minWidth: '200px', maxWidth: '200px', position: 'relative', verticalAlign: 'top', paddingTop: '8px' }}>
                <div>
                    <input
                        id={`product_id_${r.id}`} 
                        name={`product_id_${r.id}`} 
                        className="form-control common-input-all"
                        value={r.product_id || ''} 
                        onChange={e => handleUpdateRow(r.id, { product_id: e.target.value })}
                        onBlur={() => {
                            if (showEmptyFieldErrors) {
                                validateEmptyFields();
                            }
                        }} 
                        placeholder="Enter Product ID"
                        style={{ 
                            borderColor: getProductIdBorderColor(),
                            minHeight: '40px'
                        }}
                    />
                    <div style={{ color: '#DC3545', fontSize: '12px', marginTop: '4px', paddingLeft: '4px', minHeight: '18px' }}>
                        {getProductIdErrorMessage()}
                    </div>
                </div>
            </td>
            
            <td style={{ minWidth: '200px', maxWidth: '200px', position: 'relative', verticalAlign: 'top', paddingTop: '8px' }}>
                <div>
                    <input
                        id={`sku_id_${r.id}`} 
                        name={`sku_id_${r.id}`} 
                        className="form-control common-input-all"
                        value={r.sku_id || ''} 
                        onChange={e => handleUpdateRow(r.id, { sku_id: e.target.value })}
                        onBlur={() => {
                            if (showEmptyFieldErrors) {
                                validateEmptyFields();
                            }
                        }} 
                        placeholder="Enter SKU ID"
                        style={{ 
                            borderColor: getSkuIdBorderColor(),
                            minHeight: '40px'
                        }}
                    />
                    <div style={{ color: '#DC3545', fontSize: '12px', marginTop: '4px', paddingLeft: '4px', minHeight: '18px' }}>
                        {getSkuIdErrorMessage()}
                    </div>
                </div>
            </td>
            
            <td className="drag-handle" style={{ cursor: 'grab', verticalAlign: 'top', paddingTop: '12px', width: '40px' }}>
                <Menu size={18} />
            </td>
        </tr>
    );
}